import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook to handle unsaved changes warnings
 * Works with BrowserRouter (no data router required)
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes
 * @param {string} message - Custom message to show (optional)
 * @returns {Object} - { showPrompt, confirmNavigation, cancelNavigation, pendingLocation }
 */
export function useUnsavedChanges(hasUnsavedChanges, message = 'You have unsaved changes. Are you sure you want to leave?') {
  const [showPrompt, setShowPrompt] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);

  // Keep ref in sync
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Handle browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChangesRef.current) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [message]);

  // Override navigation - intercept clicks on links
  useEffect(() => {
    const handleClick = (e) => {
      // Check if it's a navigation link
      const link = e.target.closest('a[href]');
      if (!link) return;
      
      const href = link.getAttribute('href');
      // Only intercept internal navigation
      if (!href || href.startsWith('http') || href.startsWith('mailto:')) return;
      
      // Check if navigating to a different page
      if (hasUnsavedChangesRef.current && href !== location.pathname) {
        e.preventDefault();
        e.stopPropagation();
        setPendingLocation(href);
        setShowPrompt(true);
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [location.pathname]);

  const confirmNavigation = useCallback(() => {
    setShowPrompt(false);
    if (pendingLocation) {
      navigate(pendingLocation);
      setPendingLocation(null);
    }
  }, [navigate, pendingLocation]);

  const cancelNavigation = useCallback(() => {
    setShowPrompt(false);
    setPendingLocation(null);
  }, []);

  return {
    showPrompt,
    confirmNavigation,
    cancelNavigation,
    message
  };
}

export default useUnsavedChanges;
